import React, { useEffect, useState } from 'react'
import './job-schedules-list.scss'
import classnames from 'classnames'
import clone from 'clone-deep'
import * as deepEqual from 'react-fast-compare'
import DataTableView from '../../components/data-table-view/data-table-view'
import { useMountedRef } from '../../hooks/use-mounted-ref'
import { fmtDateString, fmtNumberAsDelineatedCount } from '../../helpers/formatting'
import { useAxiosCancelToken } from '../../hooks/use-axios-cancel-token'
import EntityDisplay, { EntityTitle } from '../../components/data-table-view/entity-display'
import { useService } from '@halo/hooks/use-service'
import { AppState } from '@halo/stores/app.state'
import { createJobSchedule, fetchJobScheduleFilters, fetchJobSchedules, patchSchedule } from '@halo/api/brine.api'

import {
    CheckCircleFilled,
    CopyOutlined,
    DeleteFilled,
    EditOutlined,
    Loading3QuartersOutlined,
    ReloadOutlined,
} from '@ant-design/icons'

import { Icon as LegacyIcon } from '@ant-design/compatible'
import { Button, Dropdown, Menu, notification, Radio, Table } from 'antd'
import { useLocation } from 'react-router'
import queryString from 'query-string'
import { stripUndefined } from '@halo/helpers/clean-objects'
import { collateConfig, buildApiOrderConfig } from '@halo/components/data-table-view/helpers'
import { useRefState } from '@halo/hooks/use-ref-state'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import ExpandedScheduleView from '@halo/components/job-schedules-list/expanded-schedule-view'
import { useRefEffect } from '@halo/hooks/use-ref-effect'
import { DataSourceResolver } from '@halo/components/data-table-view/types'
import {
    handleScheduleAlarmSilence,
    handleScheduleAlarmUnsilence,
    handleScheduleDestroy,
    handleScheduleDisable,
    handleScheduleEnable,
} from '@halo/components/job-schedules-list/helpers'
import { DataTableContext } from '@halo/components/data-table-view/context'
import { observer } from 'mobx-react'
import { JOB_SCHEDULE_ACTION } from '@halo/components/job-schedules-list/enums'
import JobScheduleEditor from '@halo/components/job-schedules-list/job-schedule-editor'
import * as randomstring from 'randomstring'
import { TablePaginationConfig } from 'antd/lib/table/interface'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'

const JobDateTime = observer(({ date }: { date: string }) => {
    const appState = useService<AppState>(AppState)

    return !date ? (
        <>--</>
    ) : (
        <EntityDisplay
            top={{ left: <span className="job-date">{fmtDateString(date, 'll')}</span> }}
            mid={{
                left: (
                    <span className="job-time">{fmtDateString(date, 'LT z', appState.displayTimeZone ?? 'utc')}</span>
                ),
            }}
        />
    )
})

const renderBoolean = (value: boolean) =>
    value ? <CheckCircleFilled /> : <Radio style={{ pointerEvents: 'none' }} checked={false} />

const JobSchedulesList = () => {
    const appState = useService<AppState>(AppState)

    const [_mounted, runIfMounted] = useMountedRef()
    const [editorState, setEditorState] = useState<any>({ visible: false })
    const [cancelToken, resetCancelToken] = useAxiosCancelToken()
    const { search: rawQs } = useLocation()
    const qs = queryString.parse(rawQs.replace(/^\?/, ''))
    const qsJobId = qs.job_id ?? qs.jobId

    const [schedules, setSchedules] = useRefState({
        loading: false,
        data: undefined,
        pagination: {
            pageSize: 100,
            current: 1,
            total: 100,

            size: 'small',
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showQuickJumper: true,
            showTotal: (total: number) => renderTotal(total),
            itemRender: (current: any, type: string, originalElement: any) => {
                if (type === 'prev') {
                    return <a>Previous</a>
                }
                if (type === 'next') {
                    return <a>Next</a>
                }
                if (type === 'page' || /^jump/.test(type)) {
                    return ''
                }

                return originalElement
            },
        } as TablePaginationConfig,
        filters: stripUndefined({
            tableSearchValue: qsJobId,
            enabled: ['true'],
        }),
        sortOrder: {
            columnKey: 'created_at',
            field: 'created_at',
            order: 'descend',
        },
    })

    const [paginationLinks, setPaginationLinks] = React.useState<null | IApiResponsePaginationLinks>(null)
    const [apiFilters] = useRefEffect(() => {
        return fetchJobScheduleFilters({ showLoadingScreen: false })
    }, [false])

    const renderTotal = (total: number): React.ReactNode => {
        const {
            pagination: { current, pageSize },
        } = clone(schedules.current)
        const start = fmtNumberAsDelineatedCount((current - 1) * pageSize + 1)
        const end = fmtNumberAsDelineatedCount(current * pageSize)
        const ttl = fmtNumberAsDelineatedCount(total)

        return total <= pageSize
            ? `Showing ${ttl} of ${ttl} Total Schedules(s)`
            : `Showing ${start}-${end} of ${ttl} Total Schedules(s)`
    }

    const fetchData: DataSourceResolver<any> = async (pagination, filters, sortOrder, extra): Promise<void> => {
        setSchedules({ ...schedules.current, loading: true })
        let data: any[] = []

        let paginationConfig = clone(collateConfig(pagination, schedules.current.pagination))
        let filtersConfig = clone(collateConfig(filters, schedules.current.filters))
        let sortConfig = clone(collateConfig(sortOrder, schedules.current.sortOrder))

        const pageSizeChanged = paginationConfig.pageSize !== schedules.current.pagination?.pageSize
        const filtersChanged = !deepEqual(filtersConfig, schedules.current.filters)
        const sortChanged = !deepEqual(sortConfig, schedules.current.sortOrder)
        const changesDetected = pageSizeChanged || filtersChanged || sortChanged

        // reset current page on filter/sort/size change
        if (changesDetected) {
            paginationConfig.current = 1
        }

        const searchVal = filtersConfig.tableSearchValue?.trim()
        const orderConfig = buildApiOrderConfig(sortConfig)

        const query = stripUndefined({
            limit: paginationConfig?.pageSize,
            page: paginationConfig?.current,
            search: !searchVal ? undefined : searchVal,
            target_name: !filtersConfig.target_name?.length ? undefined : filtersConfig.target_name.join(','),
            env: !filtersConfig.env?.length ? undefined : filtersConfig.env.join(','),
            enabled: !filtersConfig.enabled?.length ? undefined : filtersConfig.enabled.join(','),
            order: !orderConfig ? undefined : JSON.stringify(orderConfig),
        })

        resetCancelToken()
        const res = await fetchJobSchedules({
            query,
            showLoadingScreen: false,
            cancelToken: cancelToken.current,
        })

        if (res && res.ok) {
            data = res.data!
            setPaginationLinks(res.meta.links ?? paginationLinks)
        }

        runIfMounted(() => {
            setSchedules({
                loading: false,
                data,
                pagination: paginationConfig,
                filters: filtersConfig,
                sortOrder: sortConfig,
            })
        })
    }

    const handleRowAction = (action: string | number, schedule: any) => {
        if (action === JOB_SCHEDULE_ACTION.EDIT) {
            setEditorState({
                key: randomstring.generate(),
                visible: true,
                schedule,
            })
        } else if (action === JOB_SCHEDULE_ACTION.DUPLICATE) {
            let duplicateSchedule = clone(schedule)
            delete duplicateSchedule.id
            delete duplicateSchedule.name
            delete duplicateSchedule.destroyed_at
            delete duplicateSchedule.created_at

            setEditorState({
                key: randomstring.generate(),
                visible: true,
                isDuplicate: true,
                schedule: duplicateSchedule,
            })
        } else if (action === JOB_SCHEDULE_ACTION.ENABLE) {
            handleScheduleEnable(schedule, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        } else if (action === JOB_SCHEDULE_ACTION.DISABLE) {
            handleScheduleDisable(schedule, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        } else if (action === JOB_SCHEDULE_ACTION.DESTROY) {
            handleScheduleDestroy(schedule, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        } else if (action === JOB_SCHEDULE_ACTION.ALARMS_SILENCE) {
            handleScheduleAlarmSilence(schedule, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        } else if (action === JOB_SCHEDULE_ACTION.ALARMS_UNSILENCE) {
            handleScheduleAlarmUnsilence(schedule, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        }
    }

    // run once on initial mount
    useEffect(() => {
        fetchData()
    }, [false])

    return (
        <div className={classnames('jobs-manager-schedules-list')}>
            <DataTableContext.Provider
                value={{
                    loading: schedules.current.loading,
                    currentDataSource: schedules.current.data,
                    resolveDataSource: fetchData,
                }}
            >
                <DataTableView
                    searchPlaceholder="Search ID or name"
                    header={
                        <>
                            <span>Job Schedules</span>
                            <span>
                                <ReloadOutlined spin={schedules.current.loading} onClick={() => fetchData()} />
                            </span>
                        </>
                    }
                    filtersDefaultValue={clone(schedules.current.filters)}
                    filters={apiFilters.current?.then(({ ok, data }) => {
                        const filters: any[] = []
                        if (ok && Array.isArray(data.targets)) {
                            filters.push({
                                className: 'schedule-filter target-name-filter',
                                dataIndex: 'target_name',
                                multiple: true,
                                maxTagCount: 1,
                                placeholder: 'All targets',
                                optionPrefix: 'Target',
                                options: data.targets.map((tgt: any) => ({
                                    group: tgt.type,
                                    value: tgt.name,
                                    label: tgt.name === 'submarine-communications-cable' ? 'scc' : tgt.name,
                                })),
                            })
                        }

                        filters.push({
                            className: 'schedule-filter enabled-filter',
                            dataIndex: 'enabled',
                            multiple: true,
                            maxTagCount: 2,
                            placeholder: 'Any state',
                            optionPrefix: 'State',
                            options: [
                                { label: 'Enabled', value: 'true' },
                                { label: 'Disabled', value: 'false' },
                            ],
                        })

                        return filters
                    })}
                    actions={
                        appState.currentUser?.isInternalDevUser && [
                            <Button
                                key="new-schedule"
                                size="small"
                                type="primary"
                                onClick={() => {
                                    setEditorState({
                                        key: randomstring.generate(),
                                        visible: true,
                                    })
                                }}
                            >
                                <span>New Schedule</span>
                            </Button>,
                        ]
                    }
                >
                    <Table
                        size="small"
                        bordered={false}
                        // scroll.y used to freeze header row
                        // scroll.x used to fix bug with col width and scroll.y
                        // https://github.com/ant-design/ant-design/issues/7603#issuecomment-697600114
                        scroll={{ y: 549, x: 'max-content' }}
                        rowKey="id"
                        loading={
                            !schedules.current.loading
                                ? false
                                : {
                                      indicator: <Loading3QuartersOutlined spin={true} />,
                                  }
                        }
                        dataSource={schedules.current.data}
                        pagination={false}
                        footer={(currentTableData) => (
                            <SwSimpleLinkPagination
                                currentTableData={currentTableData}
                                links={paginationLinks}
                                onPrev={(_ev, _link, config) => {
                                    setSchedules({
                                        ...schedules.current,
                                        pagination: {
                                            ...schedules.current.pagination,
                                            current: config.current!,
                                        },
                                    })
                                    fetchData()
                                }}
                                onNext={(_ev, _link, config) => {
                                    setSchedules({
                                        ...schedules.current,
                                        pagination: {
                                            ...schedules.current.pagination,
                                            current: config.current!,
                                        },
                                    })
                                    fetchData()
                                }}
                            />
                        )}
                        locale={{
                            emptyText: <AntdTableEmptyPlaceholder text="No Schedules" icon="clock-circle" />,
                        }}
                        onChange={fetchData}
                        expandedRowRender={(schedule: any, index: number, indent: number, expanded: boolean) => (
                            <ExpandedScheduleView
                                schedule={schedule}
                                index={index}
                                indent={indent}
                                expanded={expanded}
                            />
                        )}
                    >
                        <Table.Column
                            title="Name"
                            className="name"
                            dataIndex="name"
                            render={(name, schedule: any) => {
                                return (
                                    <EntityDisplay
                                        top={{
                                            left: <EntityTitle>{name}</EntityTitle>,
                                        }}
                                        mid={{
                                            left: <span className="schedule-id">#{schedule.id}</span>,
                                        }}
                                    />
                                )
                            }}
                        />
                        <Table.Column
                            className="target"
                            title="Target"
                            dataIndex="target"
                            render={(config) => {
                                return (
                                    <EntityDisplay
                                        top={{
                                            left: (
                                                <EntityTitle>
                                                    {config.name === 'submarine-communications-cable'
                                                        ? 'scc'
                                                        : config.name}
                                                </EntityTitle>
                                            ),
                                        }}
                                        mid={{
                                            left: <span className="target-type">{config.type}</span>,
                                        }}
                                    />
                                )
                            }}
                        />
                        <Table.Column
                            className="enabled"
                            title="Enabled"
                            dataIndex="enabled"
                            align="center"
                            render={renderBoolean}
                        />
                        <Table.Column
                            className="created_at"
                            title="Created"
                            dataIndex="created_at"
                            align="center"
                            render={(dt) => <JobDateTime date={dt} />}
                        />
                        {appState.currentUser?.isInternalDevUser && (
                            <Table.Column
                                className="actions"
                                title="Actions"
                                dataIndex="actions"
                                align="center"
                                render={(_, schedule: any) => {
                                    const { enabled, alarm_config } = schedule

                                    return (
                                        <Dropdown.Button
                                            size="small"
                                            className="job-schedule-row-actions-btn"
                                            onClick={() => handleRowAction(JOB_SCHEDULE_ACTION.EDIT, schedule)}
                                            overlay={
                                                <Menu
                                                    className="job-schedule-row-actions-dropdown-menu"
                                                    onClick={({ key }) => handleRowAction(key, schedule)}
                                                >
                                                    <Menu.Item key={JOB_SCHEDULE_ACTION.DUPLICATE}>
                                                        <CopyOutlined />
                                                        <span>Duplicate</span>
                                                    </Menu.Item>
                                                    <Menu.Item
                                                        key={
                                                            enabled
                                                                ? JOB_SCHEDULE_ACTION.DISABLE
                                                                : JOB_SCHEDULE_ACTION.ENABLE
                                                        }
                                                    >
                                                        <LegacyIcon
                                                            type={enabled ? 'pause-circle' : 'play-circle'}
                                                            theme="filled"
                                                        />
                                                        <span>{enabled ? 'Disable' : 'Enable'}</span>
                                                    </Menu.Item>
                                                    {!!alarm_config && (
                                                        <Menu.Item
                                                            key={
                                                                alarm_config.silent
                                                                    ? JOB_SCHEDULE_ACTION.ALARMS_UNSILENCE
                                                                    : JOB_SCHEDULE_ACTION.ALARMS_SILENCE
                                                            }
                                                        >
                                                            <LegacyIcon type={enabled ? 'eye' : 'eye-invisible'} />
                                                            <span>
                                                                {alarm_config.silent ? 'Enable Alarm' : 'Silence Alarm'}
                                                            </span>
                                                        </Menu.Item>
                                                    )}
                                                    <Menu.Divider />
                                                    <Menu.Item key={JOB_SCHEDULE_ACTION.DESTROY}>
                                                        <DeleteFilled />
                                                        <span>Delete</span>
                                                    </Menu.Item>
                                                </Menu>
                                            }
                                        >
                                            <EditOutlined />
                                            <span>Edit</span>
                                        </Dropdown.Button>
                                    )
                                }}
                            />
                        )}
                    </Table>
                </DataTableView>
                <JobScheduleEditor
                    key={editorState.key}
                    visible={editorState.visible}
                    mode={!editorState.schedule || editorState.isDuplicate ? 'create' : 'edit'}
                    schedule={editorState.schedule}
                    onClose={() => setEditorState({ visible: false })}
                    onSubmit={async (schedule) => {
                        notification.destroy()

                        let isCreateMode = !editorState.schedule
                        let res: any = {}

                        if (editorState.isDuplicate) {
                            isCreateMode = true
                        }
                        if (isCreateMode) {
                            res = await createJobSchedule(schedule, {})
                        } else {
                            res = await patchSchedule(editorState.schedule.id, schedule, {})
                        }

                        if (res.ok) {
                            notification.success({
                                message: '',
                                description: isCreateMode
                                    ? 'Schedule successfully created.'
                                    : 'Schedule successfully updated.',
                            })

                            setEditorState({ visible: false })
                            return fetchData()
                        } else {
                            notification.error({
                                message: '',
                                description: res.error?.message ?? 'An unknown error has occurred.',
                            })
                        }
                    }}
                />
            </DataTableContext.Provider>
        </div>
    )
}

export default JobSchedulesList
